import {
  type OrgMember,
  type OrgRole,
  orgMemberSchema,
  type WorkspaceGrant,
  type WorkspaceRole,
  workspaceGrantSchema,
} from '@noconflict/contracts';
import type { Supabase } from '../../shared/api/supabase';

/** Members of an org with names and emails (`members.manage`, or `grants.manage` on one of its workspaces). */
export async function listOrgMembers(
  client: Supabase,
  organizationId: string,
): Promise<OrgMember[]> {
  const { data, error } = await client.rpc('list_org_members', {
    p_organization_id: organizationId,
  });
  if (error) throw new Error(error.message);
  return orgMemberSchema.array().parse(data);
}

/** The grants of an org that RLS shows the caller: all with `members.manage`, else those on workspaces they manage, plus their own. */
export async function listOrgGrants(
  client: Supabase,
  organizationId: string,
): Promise<WorkspaceGrant[]> {
  const { data, error } = await client
    .from('workspace_grants')
    .select('*')
    .eq('organization_id', organizationId);
  if (error) throw new Error(error.message);
  return workspaceGrantSchema.array().parse(data);
}

async function call(
  client: Supabase,
  fn:
    | 'set_org_role'
    | 'remove_member'
    | 'leave_organization'
    | 'set_workspace_grant'
    | 'revoke_workspace_grant',
  args: Record<string, string | null>,
): Promise<void> {
  // The RPC argument types differ per function; each caller below passes exactly its function's arguments.
  const { error } = await client.rpc(fn, args as never);
  if (error) throw new Error(error.message);
}

export function setOrgRole(
  client: Supabase,
  organizationId: string,
  userId: string,
  role: OrgRole,
) {
  return call(client, 'set_org_role', {
    p_organization_id: organizationId,
    p_user_id: userId,
    p_role: role,
  });
}

export function removeMember(client: Supabase, organizationId: string, userId: string) {
  return call(client, 'remove_member', { p_organization_id: organizationId, p_user_id: userId });
}

export function leaveOrganization(client: Supabase, organizationId: string) {
  return call(client, 'leave_organization', { p_organization_id: organizationId });
}

/** `workspaceId` null is the org-wide grant ("All workspaces"). Granting again replaces the role. */
export function setWorkspaceGrant(
  client: Supabase,
  target: { organizationId: string; workspaceId: string | null; userId: string },
  role: WorkspaceRole,
) {
  return call(client, 'set_workspace_grant', {
    p_organization_id: target.organizationId,
    p_workspace_id: target.workspaceId,
    p_user_id: target.userId,
    p_role: role,
  });
}

export function revokeWorkspaceGrant(client: Supabase, grantId: string) {
  return call(client, 'revoke_workspace_grant', { p_grant_id: grantId });
}

/**
 * Ownership transfer: make the other person an owner, then step down to admin. If the second step fails the
 * org simply has two owners, which is a valid state.
 */
export async function transferOwnership(
  client: Supabase,
  organizationId: string,
  fromUserId: string,
  toUserId: string,
): Promise<void> {
  await setOrgRole(client, organizationId, toUserId, 'owner');
  await setOrgRole(client, organizationId, fromUserId, 'admin');
}
